import {
  Icon,
  Modal,
} from "@twinkltech/curriculum-architect-component-library";
import { ConflictingPlanErrorResponse } from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, IconCommunicationInformation, Text } from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";

import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { CONFLICT_RESOLUTION_MODAL_NS } from "./ConflictResolitionModal.i18n";

export const ConflictResolutionModal = ({
  conflictingData,
  handleConfirm,
  onCancel,
  onClose,
}: {
  conflictingData: ConflictingPlanErrorResponse;
  /**
   * Overwrite the conflicting data with the users changes
   */
  handleConfirm: () => Promise<void>;
  /**
   * Drop the users changes and refresh with the latest data
   */
  onCancel: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation(CONFLICT_RESOLUTION_MODAL_NS);
  const { incrementEngagementCount } = useFeedback();
  const { click } = useTwinklAnalytics();

  const onOverwrite = async () => {
    incrementEngagementCount();
    await handleConfirm();
    onClose();
  };

  // 2025-12-31T24:59:59.001Z -> 15th October 2025, 11:50am
  const updateAtDate = new Date(conflictingData.updatedAt);
  const formattedDate = updateAtDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const formattedTime = updateAtDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Modal open className="px-3 py-3" width="md" onClose={onClose}>
      <Modal.Header>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <Icon className="text-[#DB2C00]" icon="InfoFilled"></Icon>
            <Modal.Title>
              <Text variant="2xl-medium">{t("title")}</Text>
            </Modal.Title>
          </div>
          <Text>{t("explanation")}</Text>
          <Text variant="sm-medium">
            {t("time-label")}:{" "}
            <span className="font-normal">{`${formattedDate}, ${formattedTime}`}</span>
          </Text>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-6">
          <div>
            <Text className="mt-4 mb-10" variant="xl-medium">
              {t("what-you-can-do.title")}
            </Text>
            <Text variant="lg-medium">{t("discard-section.title")}</Text>
            <Text variant="base-normal">
              {t("discard-section.list-points.point-1")}
            </Text>
            <Text variant="base-normal">
              {t("discard-section.list-points.point-2")}
            </Text>
          </div>

          <div>
            <Text variant="lg-medium">{t("keep-section.title")}</Text>
            <Text variant="base-normal">
              {t("keep-section.list-points.point-1")}
            </Text>
            <Text variant="base-normal">
              {t("keep-section.list-points.point-2")}
            </Text>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-neutral-100 p-200 rounded-2xl mt-6">
          <IconCommunicationInformation className="w-7" />
          <div>
            <Text as="p" className="m-0! leading-tight" variant="base-medium">
              {t("tips.title")}
            </Text>
            <ul className="list-disc ml-7 mt-1 space-y-1">
              <li>
                <Text>{t("tips.list-points.point-1")}</Text>
              </li>
              <li>
                <Text>{t("tips.list-points.point-2")}</Text>
              </li>
              <li>
                <Text>{t("tips.list-points.point-3")}</Text>
              </li>
            </ul>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-3">
          <Button
            variant="outlined"
            onClick={() => {
              incrementEngagementCount();
              click("click_cta", {
                ctaCopy: "Discard My Changes",
                clickLocation: "Conflict Resolution Modal",
              });
              onCancel();
            }}
          >
            {t("actions.keep-current")}
          </Button>
          <Button variant="error" onClick={onOverwrite}>
            {t("actions.override-current")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
